/**
 * @fileoverview Questions step component for handling probing questions in the analysis wizard
 */

import React from 'react';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import { TrebbleTitle, TrebbleText } from '../../common/TrebbleTypography.tsx';
import TrebbleCard from '../../common/TrebbleCard.tsx';
import TrebbleRadio from '../../common/TrebbleRadio.tsx';
import TrebbleInput from '../../common/TrebbleInput.tsx';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

/**
 * @typedef {import('../types/deepcut.js').ProbingQuestion} ProbingQuestion
 */

/**
 * Questions step component for gathering additional information
 * @param {Object} props - Component props
 * @param {ProbingQuestion[]} props.workingQuestions - Array of probing questions
 * @param {Object.<string, string>} props.questionAnswers - Current answers keyed by question ID
 * @param {Function} props.onQuestionAnswersChange - Callback for answer changes
 * @returns {React.ReactElement}
 */
export default function QuestionsStep({
  workingQuestions,
  questionAnswers,
  onQuestionAnswersChange,
}) {
  const updateAnswer = (questionId, answer) => {
    onQuestionAnswersChange({
      ...questionAnswers,
      [questionId]: answer
    });
  };

  return (
    <>
      <style>{`
        .deepcut-textarea::placeholder,
        textarea.deepcut-textarea::placeholder,
        .ant-input.deepcut-textarea::placeholder{
          color: var(--text-default-color, #6B7280) !important;
          opacity: 0.5 !important;
        }
        .trebble-radio, .trebble-radio-group .ant-radio-wrapper {
          color: var(--text-default-color, #111827) !important;
        }
        body.darkTheme .trebble-radio,
        body.darkTheme .trebble-radio-group .ant-radio-wrapper {
          color: white !important;
        }
      `}</style>
    <TrebbleSpace direction="vertical" style={{ width: '100%' }}>
      <div style={{ marginBottom: 24 }}>
        <TrebbleTitle level={3} style={{ margin: 0 }}>
          {window.getI18n(ti18n, 'QUESTIONS_STEP_TITLE')}
        </TrebbleTitle>
      </div>
      
      {workingQuestions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <TrebbleText type="secondary">{window.getI18n(ti18n, 'NO_ADDITIONAL_QUESTIONS')}</TrebbleText>
        </div>
      ) : (
        <TrebbleSpace direction="vertical" size="middle" style={{ width: '100%' }}>
          {workingQuestions.map((question, index) => (
            <TrebbleCard key={question.id} className="bg-gray-50 dark:bg-gray-800">
              <TrebbleSpace direction="vertical" size="small" style={{ width: '100%' }}>
                <TrebbleText strong style={{ fontSize: '16px', lineHeight: '1.4' }}>
                  {index + 1}. {question.text}
                </TrebbleText>
                {question.questionType === 'multiple-choice' && question.options ? (
                  <TrebbleRadio.Group
                    value={questionAnswers[question.id]?.startsWith("custom: ") ? "custom" : questionAnswers[question.id] || ""}
                    onChange={(e) => {
                      if (e.target.value === "custom") {
                        updateAnswer(question.id, "custom: ");
                      } else {
                        updateAnswer(question.id, e.target.value);
                      }
                    }}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    <TrebbleSpace direction="vertical" style={{ width: '100%' }}>
                      {question.options.map((option, optionIndex) => (
                        <TrebbleRadio key={optionIndex} value={option} style={{ display: 'block',  color: 'var(--text-default-color, #111827)' }}>
                          {option}
                        </TrebbleRadio>
                      ))}
                      <TrebbleRadio value="custom" style={{ display: 'block', color: 'var(--text-default-color, #111827)' }}>
                        {window.getI18n(ti18n, 'OTHER_PLEASE_SPECIFY')}
                        {(questionAnswers[question.id] === "custom: " || questionAnswers[question.id]?.startsWith("custom: ")) && (
                          <TrebbleInput
                            placeholder={window.getI18n(ti18n, 'PLEASE_SPECIFY_PLACEHOLDER')}
                            value={questionAnswers[question.id]?.startsWith("custom: ") ? questionAnswers[question.id].slice(8) : ""}
                            onChange={(e) => updateAnswer(question.id, `custom: ${e.target.value}`)}
                            style={{ marginTop: 4, marginLeft: 8 }}
                            className="deepcut-textarea"
                          />
                        )}
                      </TrebbleRadio>
                    </TrebbleSpace>
                  </TrebbleRadio.Group>
                ) : (
                  <TrebbleInput.TextArea
                    rows={2}
                    placeholder={window.getI18n(ti18n, 'YOUR_ANSWER_PLACEHOLDER')}
                    value={questionAnswers[question.id] || ""}
                    onChange={(e) => updateAnswer(question.id, e.target.value)}
                    style={{ marginTop: 8 }}
                    className="deepcut-textarea"
                  />
                )}
              </TrebbleSpace>
            </TrebbleCard>
          ))}
        </TrebbleSpace>
      )}
    </TrebbleSpace>
    </>
  );
} 